import { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import {
  getAllSavedFormData,
  getAllSubmittedFormData,
  saveOfflineForm,
  submitOfflineForm,
  submitXFAFormOffline,
  deleteSavedFormData,
  deleteSubmittedFormData,
} from "../api/endpoints/fetchTemplates";
import { useAuth } from "../contexts/AuthContext";
import { Toast } from "./ui/Toast";

interface ToastState {
  show: boolean;
  message: string;
  type: "success" | "error" | "warning";
}

export function SyncOfflineData() {
  const { user } = useAuth();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [toast, setToast] = useState<ToastState>({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (
    message: string,
    type: "success" | "error" | "warning" = "success"
  ) => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: "", type: "success" });
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      setIsOnline(false);
      // Stop syncing if user goes offline during sync
      if (isSyncing) {
        setIsSyncing(false);
        showToast("Sync interrupted: Device went offline", "warning");
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [isSyncing]);

  useEffect(() => {
    // Check pending data count
    const checkPendingData = async () => {
      try {
        const savedForms = await getAllSavedFormData();
        const submittedForms = await getAllSubmittedFormData();
        setPendingCount(savedForms.length + submittedForms.length);
      } catch (error) {
        console.error("Error checking pending data:", error);
      }
    };

    checkPendingData();
    const interval = setInterval(checkPendingData, 1000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const handleSync = async () => {
    if (!isOnline || !user) {
      console.warn("Cannot sync: Device is offline or user not logged in");
      return;
    }

    setIsSyncing(true);
    let syncedCount = 0;
    let failedCount = 0;

    try {
      // Sync saved forms
      const savedForms = await getAllSavedFormData();

      for (const form of savedForms) {
        try {
          await saveOfflineForm(form.id, form.data);

          // Delete from IndexedDB after successful sync
          await deleteSavedFormData(form.id);

          syncedCount++;
        } catch (error) {
          failedCount++;
        }
      }

      // Sync submitted forms
      const submittedForms = await getAllSubmittedFormData();

      for (const form of submittedForms) {
        try {
          // Parse the data back from encoded string
          if (form.type === "acro") {
            await submitOfflineForm(form.id, form.data);
          } else {
            await submitXFAFormOffline(form.id, form.data);
          }

          // Delete from IndexedDB after successful sync
          await deleteSubmittedFormData(form.id);

          syncedCount++;
        } catch (error) {
          failedCount++;
        }
      }
      // Update pending count
      setPendingCount(failedCount);

      if (syncedCount > 0) {
        showToast(
          `Successfully synced ${syncedCount} offline item(s)!`,
          "success"
        );
        // Refresh the page after successful sync
        setTimeout(() => {
          window.location.reload();
        }, 2000); // Give user time to see the toast
      }
      if (failedCount > 0) {
        showToast(
          `Failed to sync ${failedCount} item(s). Will retry later.`,
          "warning"
        );
      }
    } catch (error) {
      showToast("Failed to sync offline data. Please try again.", "error");
    } finally {
      setIsSyncing(false);
    }
  };

  // Don't show if no pending data and online
  if (pendingCount === 0 && isOnline) {
    return (
      <>
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={closeToast}
          />
        )}
        <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg text-white">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="text-sm">All Systems Connected</span>
        </div>
      </>
    );
  }

  return (
    <>
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={closeToast} />
      )}
      <button
        onClick={handleSync}
        disabled={isSyncing || !isOnline}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition !h-9 ${
          isSyncing
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : isOnline
            ? "bg-blue-500 text-white hover:bg-blue-600"
            : "bg-gray-400 text-gray-200 cursor-not-allowed"
        }`}
        title={
          !isOnline
            ? "Device is offline"
            : pendingCount > 0
            ? `${pendingCount} offline item(s) pending sync`
            : "All data synced"
        }
      >
        <RefreshCw className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`} />
        <span>
          {isSyncing
            ? "Syncing..."
            : !isOnline
            ? "Offline"
            : pendingCount > 0
            ? `Sync (${pendingCount})`
            : "Synced"}
        </span>
      </button>
    </>
  );
}
